import {
	addDocument,
	addMailItem,
	addMailItemDescription,
	BatchAddressType,
	BatteryDetailsType,
	ControlledMaterialUpdateType,
	createForwardingBatch,
	CreateForwardingBatchType,
	deleteForwardingBatch,
	deleteMailItemDescription,
	ExportDetailsType,
	ForwardingBatchType,
	ForwardingBatchUpdateType,
	getControlledMaterials,
	getCountryCodeRequirements,
	getCommodityCodeByReference,
	getCommodityCodes,
	getUserDocumentTypes,
	removeDocument,
	removeMailItem,
	updateAddress,
	updateBatteryDetails,
	updateControlledMaterials,
	updateExportDetails,
	updateMailItemDescription,
	updateNotes,
	updatePostage,
	updateSignature,
	useInfiniteClientNames,
	UserDocumentTypesType,
	UserFileCategory,
	useUpdateForwardingBatches,
	getCommodityCodeHeading,
	getCommodityCodeSection,
	getCommodityCodeChapter,
	updateDocumentsOnly,
	validateForwardingBatch,
	ipmQueryClient,
	uploadFile,
	QueryStatus,
} from '@ukpostbox/client-data';
import {
	BatchMailItemType,
	ControlledMaterialType,
	MailItemDescriptionForwardingBatchType,
} from '@ukpostbox/client-data/lib/Types/ForwardingBatchTypes';
import { IpmRqKeys } from '@ukpostbox/client-data/lib/Wrappers/ReactQuery/CacheKeys';
import { useForwardingBatch } from '@ukpostbox/client-data/lib/Wrappers/ReactQuery/Endpoints/ForwardingBatch';
import { SelectItemType } from '@ukpostbox/frontend-framework';
import { GetArrayIndexByKeyValue } from '@ukpostbox/helpers';
import React, { PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { HarmonySearchModal } from '../Components/HarmonyCodes/Modals/HarmonySearchModal';
import { ManualEventSetter } from '../../_Global/_e2e/Events';
import { ForwardingBatchContextType, ForwardingBatchProviderType } from '../Types/ForwardingBatchTypes';

const ForwardingBatchContext = React.createContext<ForwardingBatchContextType>({} as ForwardingBatchContextType);

function ForwardingBatchProvider(props: PropsWithChildren<ForwardingBatchProviderType>) {
	const { batchId } = useParams();

	const [harmonyCodes, setHarmonyCodes] = useState<any>([]);

	const [commodityHeading, setCommodityHeading] = useState<any>([]);
	const [commoditySubHeading, setCommoditySubHeading] = useState<any>([]);
	const [commodityChapter, setCommodityChapter] = useState<any>([]);

	const [documentsOnly, setDocumentsOnly] = useState<boolean>(false);
	const [currentBatchId, setCurrentBatchId] = useState<number>(parseInt(batchId as string, 10) || -1);

	const [commoditySearchOpen, setCommoditySearchOpen] = useState<any>(false);

	const [currentBatchAddressId, setCurrentBatchAddressId] = useState<number>(-1);
	const [currentBatchPostageId, setCurrentBatchPostageId] = useState<number>(-1);

	const [currentBatchData, setCurrentBatchData] = useState<ForwardingBatchType | undefined>(undefined);
	const [currentBatchStatus, setCurrentBatchStatus] = useState<QueryStatus>('pending');
	const [currentBatchError, setCurrentBatchError] = useState<Error | null>(null);
	const [controlledMaterials, setControlledMaterials] = useState<Array<ControlledMaterialType> | undefined>(
		undefined,
	);

	const [showEori, setShowEori] = useState<boolean>(false);
	const [showIoss, setShowIoss] = useState<boolean>(false);
	const [showHarmony, setShowHarmony] = useState<boolean>(false);
	const [showCommercialInvoice, setShowCommercialInvoice] = useState<boolean>(false);
	const [showCn22, setShowCn22] = useState<boolean>(false);
	const [containsLithiumBatteries, setContainsLithiumBatteries] = useState<boolean>(false);
	const [userDocumentTypes, setUserDocumentTypes] = useState<UserDocumentTypesType | null>(null);

	const [uploadingShippingDocument, setUploadingShippingDocument] = useState<boolean>(false);

	const [addressUpdating, setAddressUpdating] = useState<boolean>(false);
	const [postageUpdating, setPostageUpdating] = useState<boolean>(false);
	const [exportDetailsUpdating, setExportDetailsUpdating] = useState<boolean>(false);

	const [mailItemUpdating, setMailItemUpdating] = useState<boolean>(false);
	const [declarationUpdating, setDeclarationUpdating] = useState<boolean>(false);
	const [notesUpdating, setNotesUpdating] = useState<boolean>(false);

	const currentCommodityInput = useRef<HTMLInputElement | null>(null);

	const { status: batchStatus, data: batchData, error: batchError } = useForwardingBatch(currentBatchId);

	const { status: accountNamesStatus, data: accountNamesData, error: accountNamesError } = useInfiniteClientNames();

	const updateForwardingBatchData = useUpdateForwardingBatches();

	const UpdateBatchData = (data: any, updateType: ForwardingBatchUpdateType) => {
		const newData = { ...currentBatchData };

		switch (updateType) {
			case ForwardingBatchUpdateType.POSTAGE:
				if (newData && newData.address) {
					// @ts-ignore
					newData.postageId = data;
					// @ts-ignore
					setCurrentBatchData(newData);
				}
				break;
			case ForwardingBatchUpdateType.ADDRESS:
			default:
				if (newData && newData.address) {
					newData.address.addressId = data.addressId;
					newData.address.name = data.name;
					newData.address.businessName = data.businessName;
					newData.address.propertyName = data.propertyName;
					newData.address.addressLine1 = data.addressLine1;
					newData.address.addressLine2 = data.addressLine2;
					newData.address.addressLine3 = data.addressLine3;
					newData.address.postCode = data.postCode;
					newData.address.countryCode = data.countryCode;
					newData.address.formatted = '';
					// @ts-ignore
					setCurrentBatchData(newData);
				}
				break;
		}
	};

	const GetCountryCodeRequirements = (countryCode: string) => {
		getCountryCodeRequirements(countryCode).then((r: any) => {
			setShowEori(r.eoriRequired);
			setShowIoss(r.iossRequired);
			setShowHarmony(r.harmonyCodesRequired);
			setShowCn22(r.cn23Required);
			setShowCommercialInvoice(r.cn23Required);
		});
	};

	const UpdateAssignedAddress = (addressData: BatchAddressType) => {
		if (addressData.addressId !== currentBatchData?.address.addressId) {
			setAddressUpdating(true);

			updateForwardingBatchData.mutate({
				batchId: currentBatchId,
				newData: { address: addressData },
				updateType: ForwardingBatchUpdateType.ADDRESS,
			});

			updateAddress(currentBatchId, addressData.addressId)
				.then((r) => {
					if (addressData.countryCode !== currentBatchData?.address.countryCode) {
						GetCountryCodeRequirements(addressData.countryCode);
					}
					UpdateBatchData(addressData, ForwardingBatchUpdateType.ADDRESS);
				})
				.finally(() => setAddressUpdating(false));
		}
	};

	const UpdateAssignedPostage = (postageOption: SelectItemType) => {
		if (postageOption.value !== currentBatchData?.postageOption?.id) {
			setPostageUpdating(true);

			updateForwardingBatchData.mutate({
				batchId: currentBatchId,
				newData: { postageOption: { id: postageOption.value, value: postageOption.label } },
				updateType: ForwardingBatchUpdateType.POSTAGE,
			});

			updatePostage(currentBatchId, postageOption.value)
				.then((r) => {
					UpdateBatchData(postageOption.value, ForwardingBatchUpdateType.POSTAGE);
				})
				.finally(() => setPostageUpdating(false));
		}
	};

	const GetCommodityCodes = (searchTerm?: string) => {
		getCommodityCodes(searchTerm).then((r) => {
			console.log('GetCommodityCodes', r);
			setHarmonyCodes(r);
		});
	};
	const GetCommodityCodeByReference = (commodityReference: string) => {
		getCommodityCodeByReference(commodityReference).then((r) => {
			console.log('GetCommodityCodeByReference', r);
		});
	};

	const GetCommodityCodeHeading = (headingId: string) => {
		getCommodityCodeHeading(headingId).then((r) => {
			console.log('GetCommodityCodeHeading', r);
			setCommodityHeading(r);
		});
	};

	const GetCommodityCodeSection = (sectionId: string) => {
		getCommodityCodeSection(sectionId).then((r) => {
			console.log('GetCommodityCodeSection', r);
			setCommoditySubHeading(r);
		});
	};

	const GetCommodityCodeChapter = (chapterId: string) => {
		getCommodityCodeChapter(chapterId).then((r) => {
			console.log('GetCommodityCodeChapter', r);
			setCommodityChapter(r);
		});
	};

	const CreateForwardingBatch = (data: CreateForwardingBatchType) => {
		createForwardingBatch(data).then((r) => {
			console.log('CreateForwardingBatch', r);
		});
	};

	/*    const GetForwardingBatch = (batchId: number) => {
        getForwardingBatch(batchId).then((r) => {
            console.log('GetForwardingBatch', r);
        });
    } */

	const DeleteForwardingBatch = () => {
		deleteForwardingBatch(currentBatchId).then((r) => {
			console.log('DeleteForwardingBatch', r);
		});
	};

	const AddMailItemToBatch = (data: Array<string>, mailItems: Array<BatchMailItemType>) => {
		setMailItemUpdating(true);

		updateForwardingBatchData.mutate({
			batchId: currentBatchId,
			newData: { mailItems },
			updateType: ForwardingBatchUpdateType.ADD_ITEM,
		});

		addMailItem(currentBatchId, data)
			.then((r) => {
				console.log('AddMailItemToBatch', r);
			})
			.finally(() => setMailItemUpdating(false));
	};

	const RemoveMailItemFromBatch = (mailUuid: string) => {
		updateForwardingBatchData.mutate({
			batchId: currentBatchId,
			newData: { mailItemUuid: mailUuid },
			updateType: ForwardingBatchUpdateType.REMOVE_ITEM,
		});

		removeMailItem(currentBatchId, mailUuid).then((r) => {
			ipmQueryClient.invalidateQueries({ queryKey: [IpmRqKeys.MAIL_BOX_KEY, 'forwarding-search'] });
			console.log('RemoveMailItemFromBatch', r);
		});
	};

	const AddMailItemDescription = async (mailUuid: string, data: MailItemDescriptionForwardingBatchType) => {
		setMailItemUpdating(true);
		/*        return axiosAuthorised.post(
            `https://apicorestagingap.ipostalmail.net/client/v1.1/forwarding-batches/${currentBatchId}/items/${mailUuid}/descriptions`,
            JSON.stringify(data),
            {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'Authorization': `Bearer ${token}`
                },
            },
        )
            .then((res) => {
                console.log('batch description res', res.data);
                return res.data;
            }); */

		return addMailItemDescription(currentBatchId, mailUuid, data)
			.then((r) => {
				console.log('AddMailItemDescription', r);
				return r;
			})
			.finally(() => setMailItemUpdating(false));
	};

	const UpdateMailItemDescription = (
		mailUuid: string,
		descriptionId: number,
		data: MailItemDescriptionForwardingBatchType,
	) => {
		setMailItemUpdating(true);

		return updateMailItemDescription(currentBatchId, mailUuid, descriptionId, data)
			.then((r) => {
				console.log('UpdateMailItemDescription', r);
				return true;
			})
			.catch((ex) => {
				console.log('UpdateMailItemDescription EX', ex);
				return false;
			})
			.finally(() => setMailItemUpdating(false));
	};

	const DeleteMailItemDescription = (mailUuid: string, descriptionId: number) => {
		deleteMailItemDescription(currentBatchId, mailUuid, descriptionId).then((r) => {
			console.log('DeleteMailItemDescription', r);
		});
	};

	const AddDocumentToBatch = (file: File, documentType: number, successCallback: any) => {
		setUploadingShippingDocument(true);

		uploadFile({
			file,
			fileCategory: UserFileCategory.Forwarding,
		})
			.then((r) => {
				console.log('addUserFile', r);
				if (r.fileUuid) {
					updateForwardingBatchData.mutate({
						batchId: currentBatchId,
						newData: {
							document: {
								id: file.size,
								shippingDocumentType:
									userDocumentTypes?.documentTypes[
										GetArrayIndexByKeyValue(userDocumentTypes.documentTypes, 'value', documentType)
									].label || '',
								dateCreated: new Date(),
								file: { fileName: file.name, linkToFile: '', status: [''], tokenRequired: false },
							},
						},
						updateType: ForwardingBatchUpdateType.ADD_DOCUMENT,
					});

					addDocument(currentBatchId, r.fileUuid, documentType)
						.then()
						.finally(() => {
							setUploadingShippingDocument(false);
							successCallback();
						});
				}
			})
			.catch((e) => {
				console.log('addUserFile ex', e);
				setUploadingShippingDocument(false);
			});
	};

	const RemoveDocumentFromBatch = (documentId: number) => {
		updateForwardingBatchData.mutate({
			batchId: currentBatchId,
			newData: { documentId },
			updateType: ForwardingBatchUpdateType.REMOVE_DOCUMENT,
		});

		removeDocument(currentBatchId, documentId.toString()).then((r) => {
			console.log('RemoveDocumentFromBatch', r);
		});
	};

	const GetControlledMaterials = () => {
		getControlledMaterials(currentBatchId).then((r) => {
			setControlledMaterials(r);
			console.log('GetControlledMaterials', r);
		});
	};

	const UpdateControlledMaterials = (materialId: number, data: ControlledMaterialUpdateType) => {
		setDeclarationUpdating(true);

		updateControlledMaterials(currentBatchId, materialId, data)
			.then((r) => {
				console.log('UpdateControlledMaterials', r);
			})
			.finally(() => setDeclarationUpdating(false));
	};

	const CheckSectionsToShow = (data: ForwardingBatchType) => {
		setDocumentsOnly(data.documentsOnly);

		if (data.controlledMaterials) {
			const batteryIndex = GetArrayIndexByKeyValue(data.controlledMaterials, 'id', 12);
			setContainsLithiumBatteries(data.controlledMaterials[batteryIndex].declared);
		}

		if (data.documentationRequirements.cn23Required) {
			setShowCn22(true);
			setShowCommercialInvoice(true);
		}

		if (data.documentationRequirements.iossRequired) {
			setShowIoss(true);
		}

		if (data.documentationRequirements.eoriRequired) {
			setShowEori(true);
		}

		if (data.documentationRequirements.harmonyCodesRequired) {
			setShowHarmony(true);
		}
	};

	const UpdateLithiumBatteries = (value: boolean) => {
		setContainsLithiumBatteries(value);
	};

	const CheckItemDocumentsOnly = () => {
		// UpdateDocumentsOnly();
	};

	const UpdateDocumentsOnly = (value: boolean) => {
		console.log('UpdateDocumentsOnly', value);
		setDocumentsOnly(value);

		updateDocumentsOnly(currentBatchId, value, undefined, { stub: 'ch' }).then((r) => {
			console.log('UpdateDocumentsOnly resp', r);
		});
	};

	const UpdateNotes = (value: string) => {
		setNotesUpdating(true);
		updateNotes(currentBatchId, value, undefined, { stub: 'ch' })
			.then((r) => {
				console.log('UpdateNotes resp', r);
			})
			.finally(() => setNotesUpdating(false));
	};

	const UpdateSignature = (signatureId: number) => {
		updateSignature(currentBatchId, signatureId, undefined, { stub: 'ch' }).then((r) => {
			console.log('UpdateSignature resp', r);
		});
	};

	const GetUserDocumentTypes = () => {
		getUserDocumentTypes().then((r: any) => {
			console.log('GetUserDocumentTypes', r.data);
			setUserDocumentTypes(r.data);
		});
	};

	const UpdateExportDetails = async (data: ExportDetailsType) => {
		const r = await updateExportDetails(currentBatchId, data);
		console.log('UpdateExportDetails resp', r);
		return r;
	};

	const UpdateBatteryDeclaration = async (data: BatteryDetailsType) => {
		const r = await updateBatteryDetails(currentBatchId, data);
		console.log('UpdateBatteryDeclaration resp', r);
		return r;
	};

	const ValidateForwardingBatch = async () => {
		const r = await validateForwardingBatch(currentBatchId);
		console.log('ValidateBatch resp', r);
		return r;
	};

	const OpenHarmonyLookup = (inputRef: HTMLInputElement | null) => {
		currentCommodityInput.current = inputRef;
		setCommoditySearchOpen(true);
	};

	const HandleCommodityUpdate = (code: string) => {
		if (currentCommodityInput.current) {
			ManualEventSetter('input', currentCommodityInput.current, code);
		}

		setCommoditySearchOpen(false);
	};

	useEffect(() => {
		setCurrentBatchStatus(batchStatus);

		if (batchError) {
			console.log('error', batchError);
			setCurrentBatchError(batchError);
		}

		if (batchStatus === 'success') {
			setCurrentBatchAddressId(batchData?.address.addressId);
			setCurrentBatchPostageId(batchData?.postageOption.id);
			setCurrentBatchData(batchData);
			CheckSectionsToShow(batchData);
		}
	}, [batchData, batchStatus, batchError]);

	useEffect(() => {
		setCurrentBatchId(parseInt(batchId as string, 10) || -1);
	}, [batchId]);

	useEffect(() => {
		GetUserDocumentTypes();
	}, []);

	const forwardingBatchProviderMemo = useMemo(
		() => ({
			harmonyCodes,
			commodityHeading,
			commoditySubHeading,
			commodityChapter,

			currentBatchId,

			currentBatchAddressId,
			currentBatchPostageId,

			currentBatchData,
			currentBatchStatus,
			currentBatchError,

			accountNamesData,
			accountNamesStatus,
			accountNamesError,

			controlledMaterials,

			documentsOnly,
			showEori,
			showIoss,
			showHarmony,
			showCommercialInvoice,
			showCn22,
			containsLithiumBatteries,
			userDocumentTypes,

			addressUpdating,
			postageUpdating,
			exportDetailsUpdating,

			GetCommodityCodes,
			GetCommodityCodeByReference,

			GetCommodityCodeHeading,
			GetCommodityCodeSection,
			GetCommodityCodeChapter,

			UpdateAssignedAddress,
			UpdateAssignedPostage,

			CreateForwardingBatch,
			//    GetForwardingBatch,
			DeleteForwardingBatch,

			AddMailItemToBatch,
			RemoveMailItemFromBatch,

			AddMailItemDescription,
			UpdateMailItemDescription,
			DeleteMailItemDescription,

			AddDocumentToBatch,
			RemoveDocumentFromBatch,

			GetControlledMaterials,
			UpdateControlledMaterials,

			UpdateExportDetails,
			UpdateBatteryDeclaration,

			ValidateForwardingBatch,

			OpenHarmonyLookup,
			GetCountryCodeRequirements,
			UpdateLithiumBatteries,
			GetUserDocumentTypes,

			UpdateDocumentsOnly,
			UpdateNotes,
			UpdateSignature,

			uploadingShippingDocument,

			mailItemUpdating,
			declarationUpdating,
			notesUpdating,
		}),
		[
			harmonyCodes,
			commodityHeading,
			commoditySubHeading,
			commodityChapter,

			addressUpdating,
			postageUpdating,
			exportDetailsUpdating,
			userDocumentTypes,
			uploadingShippingDocument,
			currentBatchId,
			currentBatchAddressId,
			currentBatchPostageId,
			currentBatchData,
			currentBatchStatus,
			currentBatchError,
			accountNamesData,
			accountNamesStatus,
			accountNamesError,
			controlledMaterials,
			documentsOnly,
			showEori,
			showIoss,
			showHarmony,
			showCommercialInvoice,
			showCn22,
			containsLithiumBatteries,

			mailItemUpdating,
			declarationUpdating,
			notesUpdating,
		],
	);

	return (
		<ForwardingBatchContext.Provider value={forwardingBatchProviderMemo}>
			{props.children}
			<HarmonySearchModal
				getCommodityCodes={(searchTerm) => GetCommodityCodes(searchTerm)}
				getCommodityCodeHeading={(headingId) => GetCommodityCodeHeading(headingId)}
				harmonyCodes={harmonyCodes}
				commodityHeading={commodityHeading}
				isOpen={commoditySearchOpen}
				actionedCallback={(code: string, input: any) => HandleCommodityUpdate(code)}
				closeCallback={() => setCommoditySearchOpen(false)}
			/>
		</ForwardingBatchContext.Provider>
	);
}

const useForwardingBatchProvider = () => useContext(ForwardingBatchContext);

export { ForwardingBatchContext, useForwardingBatchProvider };

export default ForwardingBatchProvider;
